/**
 * 棒グラフ（t検定・ANOVA）のグラフ設定を中央ペインと右ペインで共有する
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useKeisukeDashboardStore } from '../../../../stores/systems/keisuke/dashboardStore';
import {
  type DashboardBarChartSettings,
  groupDisplayForTTestInferential,
  loadBarChartSettings,
  saveBarChartSettings,
} from './keisukeDashboardBarChartSettings';

/** 新規／サンプル読込でグラフ種類を既定（棒）へ戻す */
export const KEISUKE_DASHBOARD_NEW_EVENT = 'keisuke-dashboard-new';
export const KEISUKE_DASHBOARD_SAMPLE_LOADED_EVENT = 'keisuke-dashboard-sample-loaded';

type BarChartSettingsContextValue = {
  chartSettings: DashboardBarChartSettings;
  setChartSettings: Dispatch<SetStateAction<DashboardBarChartSettings>>;
};

const BarChartSettingsContext = createContext<BarChartSettingsContextValue | null>(null);

function resetCategoryPlotKindToBar(
  setChartSettings: Dispatch<SetStateAction<DashboardBarChartSettings>>
): void {
  setChartSettings((s) =>
    s.plot.categoryPlotKind === 'bar'
      ? s
      : { ...s, plot: { ...s.plot, categoryPlotKind: 'bar' } }
  );
}

export function KeisukeDashboardBarChartSettingsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [chartSettings, setChartSettings] = useState<DashboardBarChartSettings>(() =>
    loadBarChartSettings(t)
  );
  const analysisType = useKeisukeDashboardStore((s) => s.analysisOptions.type);
  const ttestInferential = useKeisukeDashboardStore(
    (s) => s.analysisOptions.ttestInferential ?? 'parametric'
  );

  useEffect(() => {
    saveBarChartSettings(chartSettings);
  }, [chartSettings]);

  /** t検定: ノンパラ↔パラメトリックで棒中心・誤差棒を結果表記と揃える */
  useEffect(() => {
    if (analysisType !== 't-test') return;
    const next = groupDisplayForTTestInferential(ttestInferential === 'parametric');
    setChartSettings((s) => {
      if (
        s.groupDisplay.barCenter === next.barCenter &&
        s.groupDisplay.errorBarType === next.errorBarType
      ) {
        return s;
      }
      return { ...s, groupDisplay: { ...s.groupDisplay, ...next } };
    });
  }, [analysisType, ttestInferential]);

  const onWorkspaceReset = useCallback(() => {
    resetCategoryPlotKindToBar(setChartSettings);
  }, []);

  useEffect(() => {
    window.addEventListener(KEISUKE_DASHBOARD_NEW_EVENT, onWorkspaceReset);
    window.addEventListener(KEISUKE_DASHBOARD_SAMPLE_LOADED_EVENT, onWorkspaceReset);
    return () => {
      window.removeEventListener(KEISUKE_DASHBOARD_NEW_EVENT, onWorkspaceReset);
      window.removeEventListener(KEISUKE_DASHBOARD_SAMPLE_LOADED_EVENT, onWorkspaceReset);
    };
  }, [onWorkspaceReset]);

  return (
    <BarChartSettingsContext.Provider value={{ chartSettings, setChartSettings }}>
      {children}
    </BarChartSettingsContext.Provider>
  );
}

export function useBarChartSettings(): BarChartSettingsContextValue {
  const ctx = useContext(BarChartSettingsContext);
  if (!ctx) {
    throw new Error(
      'useBarChartSettings must be used within KeisukeDashboardBarChartSettingsProvider'
    );
  }
  return ctx;
}
